import { useTheme } from "@/theme/ThemeProvider";
import type { Theme } from "@/theme/themes";
import { Text, View, type ViewStyle } from "react-native";

export type StatusChipTone =
  | "primary"
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info";

const STATUS_TONE: Record<string, StatusChipTone> = {
  in_care: "primary",
  hibernating: "info",
  released: "success",
  deceased: "neutral",
  voided: "danger",
};

export function statusToneFor(status: string): StatusChipTone {
  return STATUS_TONE[status] ?? "neutral";
}

/**
 * Primary auf der getönten accentBg-Fläche, nur Care/Klar.
 * Dark nutzt accent. Light dunkelt nur den Chip-Text ab.
 * Die Markenfarbe primary bleibt unverändert. Standard bleibt auf primary.
 */
function chipPrimaryText(theme: Theme): string {
  const primary = theme.color.primary.toLowerCase();
  if (theme.mode === "dark") {
    if (primary === "#0d9488") return theme.color.accent;
    return theme.color.primary;
  }
  if (primary === "#0f766e") return "#0F726B";
  return theme.color.primary;
}

export function statusChipPalette(
  theme: Theme,
): Record<StatusChipTone, { bg: string; fg: string; border: string }> {
  return {
    primary: {
      bg: theme.color.accentBg,
      fg: chipPrimaryText(theme),
      border: theme.color.accentMuted,
    },
    neutral: {
      bg: theme.color.badgeMutedBg,
      fg: theme.color.textSecondary,
      border: theme.color.border,
    },
    success: {
      bg: theme.color.successBg,
      fg: theme.color.successText,
      border: theme.color.successBg,
    },
    warning: {
      bg: theme.color.warningBg,
      fg: theme.color.warningText,
      border: theme.color.warningBg,
    },
    danger: {
      bg: theme.color.dangerBg,
      fg: theme.color.dangerText,
      border: theme.color.dangerBg,
    },
    info: {
      bg: theme.color.infoBg,
      fg: theme.color.infoText,
      border: theme.color.infoBg,
    },
  };
}

/**
 * Kompakter Status-/Filter-Chip aus Theme-Tokens.
 */
export function StatusChip({
  label,
  tone = "neutral",
  active = true,
  size = "md",
  style,
}: {
  label: string;
  tone?: StatusChipTone;
  /** false = ruhiger Outline-Zustand (Filter aus) */
  active?: boolean;
  size?: "sm" | "md";
  style?: ViewStyle;
}) {
  const { theme } = useTheme();
  const p = statusChipPalette(theme)[tone];
  const padH = size === "sm" ? theme.space.sm : 10;
  const padV = size === "sm" ? 2 : 4;
  const fontSize = size === "sm" ? theme.fontSize.tiny : theme.fontSize.small;

  return (
    <View
      style={[
        {
          paddingHorizontal: padH,
          paddingVertical: padV,
          borderRadius: theme.radius.sm,
          borderWidth: 1,
          borderColor: active ? p.border : theme.color.border,
          backgroundColor: active ? p.bg : theme.color.surface,
        },
        style,
      ]}
    >
      <Text
        style={{
          color: active ? p.fg : theme.color.textSecondary,
          fontSize,
          fontWeight: "700",
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
    </View>
  );
}
